// Pure calculation module — no Phaser dependency
// Importable by EconomySystem and UI components

import { useGameStore } from '@/store/gameStore'
import { SERVICES, ServiceDefinition, getMarketPrice } from '@/config/servicesConfig'
import { WEATHER_DEFINITIONS, WeatherType } from '@/config/eventsConfig'
import { SEASONAL_EVENTS } from '@/config/seasonalConfig'
import { getDifficultyMods } from '@/config/difficultyConfig'
import { getRegionMods } from '@/config/regionConfig'
import { getDivisionOrNone, getDivisionDemandMult } from '@/config/federationConfig'
import { collectModifiers, sumAdds, productMults, type SourcedModifier } from './ModifierSystem'
import { getFrontDeskEffects } from './staffEffects'

export interface DemandResult {
  serviceId: string
  baseDemand: number
  finalDemand: number // 0-1 (percentage)
  factors: {
    reputation: number
    season: number
    weather: number
    price: number
    facilities: number
  }
  estimatedDailyIncome: number
}

/** Get seasonal customer multiplier for current game month */
function getSeasonalMultiplier(month: number): number {
  const calendarMonth = ((month - 1) % 12) + 1
  let multiplier = 1

  for (const event of SEASONAL_EVENTS) {
    const eventEndMonth = event.startMonth + Math.ceil(event.duration / 30)
    if (calendarMonth >= event.startMonth && calendarMonth < eventEndMonth) {
      for (const effect of event.effects) {
        if (effect.type === 'customer_multiplier') multiplier *= effect.value
      }
    }
  }
  // The region's tourist seasonality amplifies (>1) or dampens (<1) the swing.
  // costa_sol = 1 → identity, so the deterministic suite is unaffected.
  return 1 + (multiplier - 1) * getRegionMods().touristSeasonality
}

/** Check if a service's requirements are met */
export function areRequirementsMet(service: ServiceDefinition): boolean {
  const store = useGameStore.getState()

  for (const req of service.requirements) {
    switch (req.type) {
      case 'courts':
        if (store.courts.length < (req.value as number)) return false
        break
      case 'building':
        if (!store.buildings.some(b => b.type === req.value)) return false
        break
      case 'staff':
        if (!store.staff.some(s => s.type === req.value)) return false
        break
      case 'reputation':
        if (store.reputation < (req.value as number)) return false
        break
    }
  }
  return true
}

/** Calculate demand for a single service */
export function calculateServiceDemand(serviceId: string, customPrice?: number): DemandResult {
  const store = useGameStore.getState()
  const service = SERVICES.find(s => s.id === serviceId)

  if (!service) {
    return { serviceId, baseDemand: 0, finalDemand: 0, factors: { reputation: 1, season: 1, weather: 1, price: 1, facilities: 1 }, estimatedDailyIncome: 0 }
  }

  // Base demand depends on game progression
  const courtCount = store.courts.length
  const baseDemand = Math.min(0.6, 0.3 + courtCount * 0.05)

  // Reputation factor: +/- 15% per star from 3
  const repFactor = 1 + (store.reputation - 3) * service.reputationWeight

  // Seasonal factor
  const seasonFactor = getSeasonalMultiplier(store.month)

  // Weather factor (only affects court-based services)
  let weatherFactor = 1
  if (service.category === 'court' || service.category === 'class') {
    const weatherDef = WEATHER_DEFINITIONS[store.currentWeather as WeatherType]
    const outdoorCourts = store.courts.filter(c => c.type === 'outdoor').length
    const indoorCourts = store.courts.filter(c => c.type === 'indoor').length
    const totalCourts = outdoorCourts + indoorCourts
    if (totalCourts > 0) {
      const outdoorMod = weatherDef?.outdoorModifier ?? 1
      // Weighted average of indoor (1.0) and outdoor (weather-affected)
      weatherFactor = (indoorCourts * 1 + outdoorCourts * outdoorMod) / totalCourts
    }
  }

  // Price factor: deviation from market price
  const currentPrice = customPrice ?? (store.servicePrices?.[serviceId] ?? service.basePrice)
  const marketPrice = getMarketPrice(serviceId)
  const priceDeviation = (currentPrice - marketPrice) / marketPrice
  const priceFactor = Math.max(0.3, 1 - priceDeviation * (service.priceWeight * 50))

  // Facilities factor: the modifier engine sums each facility's demand
  // contribution (declared per level in the registry, weighted by condition
  // at collection). Legacy buildings declare exactly the old 0.03×level, so
  // old saves produce identical numbers. Staff terms stay here until the
  // staff module migrates onto the engine.
  const mods = collectModifiers()
  const staffCount = store.staff.length
  const upgradeCount = Object.values(store.courtUpgrades).flat().length
  const facilitiesFactor = 1
    + sumAdds(mods, 'demand.all')
    + staffCount * 0.02
    + upgradeCount * 0.01
    + getFrontDeskEffects(store.staff).demandBonus

  // Active effects demand modifier (event effects map to demand.all mults)
  const effectsDemandMod = productMults(mods, 'demand.all')

  // League prestige pulls extra public (identity when no league / Cuarta)
  const prestigeMod = getDivisionDemandMult(getDivisionOrNone(store.league))

  // Calculate final demand (clamped 0.05 - 0.95)
  const rawDemand = baseDemand * repFactor * seasonFactor * weatherFactor * priceFactor * facilitiesFactor * effectsDemandMod * prestigeMod * getDifficultyMods().demandMultiplier * getRegionMods().demandMultiplier
  const finalDemand = Math.min(0.95, Math.max(0.05, rawDemand))

  // Estimate daily income
  const estimatedDailyIncome = calculateServiceIncome(service, finalDemand, currentPrice, store, mods)

  return {
    serviceId,
    baseDemand,
    finalDemand,
    factors: {
      reputation: repFactor,
      season: seasonFactor,
      weather: weatherFactor,
      price: priceFactor,
      facilities: facilitiesFactor,
    },
    estimatedDailyIncome,
  }
}

// Court rental assumed near-full bookable hours per court per day, which made
// a mid-game club net ~12k/week on Normal — money was never a constraint and
// difficulty barely mattered. This factor brings rental income down so the
// economy has real tension (Normal stays the identity difficulty profile; the
// pressure comes from the base numbers, not from a 'normal' multiplier).
const COURT_RENTAL_BALANCE = 0.6

/** Calculate income for a service given demand level */
function calculateServiceIncome(
  service: ServiceDefinition,
  demand: number,
  price: number,
  store: ReturnType<typeof useGameStore.getState>,
  mods: SourcedModifier[] = collectModifiers(),
): number {
  switch (service.category) {
    case 'court': {
      // Court rental: demand * hours * effective price per court. Courts
      // whose upgrade works are in progress are closed (isOperational ===
      // false) and don't contribute. Premium upgrades (superficie, cristal,
      // techo) add €/h on top of THEIR court's price.
      const operationalCourts = store.courts.filter(c => c.isOperational !== false)
      const totalSlotHours = service.timeSlots?.reduce((sum, slot) => {
        return sum + Math.floor(slot.hours * demand) * slot.priceMultiplier
      }, 0) ?? 0
      const priceSum = operationalCourts.reduce(
        (sum, c) => sum + price + sumAdds(mods, 'court.premium_price', c.id), 0,
      )
      return totalSlotHours * priceSum * COURT_RENTAL_BALANCE
    }
    case 'class': {
      // Classes: sessions per day * students * price, boosted by the academy
      const sessionsPerDay = Math.floor((service.maxPerDay ?? 1) * demand)
      const studentsPerSession = Math.floor(4 * demand) + 1 // 1-4 students
      return sessionsPerDay * studentsPerSession * price * productMults(mods, 'income.class')
    }
    case 'event': {
      // Events: probability of event happening * price
      const eventChance = Math.min(0.8, demand)
      return eventChance * price
    }
    case 'retail': {
      // Retail: base income * demand multiplier * barista bonus * facility
      // retail multipliers (Pro Shop / cafetería at high levels)
      const staffBonus = service.id === 'cafeteria'
        ? getFrontDeskEffects(store.staff).cafeMultiplier
        : 1
      return price * demand * 2 * staffBonus * productMults(mods, 'income.retail')
    }
    default:
      return 0
  }
}

/** Calculate all service demands at once */
export function calculateAllDemands(): DemandResult[] {
  const store = useGameStore.getState()
  const enabledServices = store.enabledServices ?? []

  return enabledServices
    .map(serviceId => calculateServiceDemand(serviceId))
    .filter(d => d.finalDemand > 0)
}

/** Get total estimated daily income from all enabled services */
export function getTotalEstimatedIncome(): number {
  return calculateAllDemands().reduce((sum, d) => sum + d.estimatedDailyIncome, 0)
}
